<script setup>
defineOptions({ name: "SolutionKaliPetPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DocumentCheckIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  HomeModernIcon as HomeModernIconOutline,
  TruckIcon as TruckIconOutline,
  TagIcon as TagIconOutline,
  ScaleIcon,
} from "@heroicons/vue/24/outline";
import {
  ShoppingBagIcon,
  HeartIcon,
  SparklesIcon,
  HomeModernIcon,
  TruckIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kalipet.webp";
import visaoGeralImg from "@/assets/visaogeralpet.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Petshop / Clínica veterinária" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Pet" (docs/books/KALI PET - EBOOK.pdf).
const segmentos = [
  { icon: ShoppingBagIcon, label: "Pet shops" },
  { icon: HeartIcon, label: "Clínicas veterinárias" },
  { icon: SparklesIcon, label: "Banho e tosa" },
  { icon: HomeModernIcon, label: "Hospedagem e creche" },
  { icon: TruckIcon, label: "Delivery e loja virtual" },
];

const destaques = [
  {
    eyebrow: "Agenda",
    title: "Agenda inteligente e otimizada",
    text: "Agendamentos recorrentes, registro de faltas e comunicação com o cliente direto pelo WhatsApp.",
  },
  {
    eyebrow: "Prontuário",
    title: "Prontuário eletrônico completo",
    text: "Registre informações, anexe imagens e exames para acompanhar todo o histórico do pet.",
  },
  {
    eyebrow: "Hospedagem",
    title: "Controle total da hospedagem",
    text: "Todas as reservas de hospedagem do pet ficam registradas no sistema, sem risco de esquecimento.",
  },
  {
    eyebrow: "Delivery",
    title: "Delivery com integração iFood Pet",
    text: "Controle a entrega dos seus produtos direto pelo sistema, com painel do entregador em tempo real.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "CT-e", "CT-e OS", "MDF-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

const modulos = [
  {
    icon: CalendarDaysIcon,
    title: "Agenda inteligente",
    subtitle: "Sem falta e sem esquecimento",
    items: [
      "Agendamentos recorrentes",
      "Registro de faltas",
      "Lembretes por WhatsApp",
    ],
  },
  {
    icon: DocumentTextIcon,
    title: "Prontuário eletrônico",
    subtitle: "Histórico completo do pet",
    items: [
      "Anexos e exames",
      "Histórico completo do pet",
      "Compartilhamento entre profissionais",
    ],
  },
  {
    icon: HomeModernIconOutline,
    title: "Hospedagem do pet",
    subtitle: "Reservas sob controle",
    items: ["Reservas registradas no sistema", "Controle de permanência"],
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: "Controle de atendimento",
    subtitle: "Tudo registrado, nada esquecido",
    items: [
      "Vacinas, exames e cirurgias",
      "Banho e tosa",
      "Lembretes via WhatsApp",
    ],
  },
  {
    icon: TagIconOutline,
    title: "Cadastro de raça, espécie e pet",
    subtitle: "Um perfil completo por pet",
    items: [
      "Ração de preferência",
      "Aviso de aniversário",
      "Vacinas e fotos",
    ],
  },
  {
    icon: TruckIconOutline,
    title: "Delivery + iFood Pet",
    subtitle: "Controle da entrega, ponta a ponta",
    items: ["Integração com iFood Pet", "Painel do entregador em tempo real"],
  },
  {
    icon: ShieldCheckIcon,
    title: "Vacinas e internações",
    subtitle: "Protocolos sem complicação",
    items: [
      "Protocolos de vacina",
      "Histórico de internações",
      "Prescrição de medicamentos",
    ],
  },
  {
    icon: BanknotesIcon,
    title: "Gestão financeira",
    subtitle: "Controle total do seu caixa",
    items: [
      "Boleto integrado ao banco",
      "PIX dinâmico",
      "Controle de caixa por profissional",
    ],
  },
];

useSeo({
  title: "Sistema para Pet Shops e Clínicas Veterinárias | Kali Pet",
  description:
    "Controle atendimentos, vacinas, estoque e vendas com o Kali Pet. Sistema completo para pet shops e clínicas veterinárias. Teste grátis!",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Pet",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-pet",
    description:
      "Sistema de gestão para pet shops, clínicas veterinárias, banho e tosa e lojas de produtos para animais. Organiza agendamentos, prontuários, estoque, caixa, vendas e vacinas com integração fiscal e acesso online.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Pet: Sistema de Gestão para Pet Shops, Clínicas Veterinárias e Banho & Tosa"
      description="Controle atendimentos, vacinas, estoque, vendas e serviços com um sistema simples, completo e 100% online. Ideal para quem cuida de pets com carinho e profissionalismo."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-pet"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Pet"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para quem cuida de pets todos os dias
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            O Kali Pet reúne agenda, prontuário eletrônico, hospedagem,
            vacinas e frente de caixa em uma única plataforma, com emissão
            fiscal integrada. Simples no dia a dia, mas completo o
            suficiente para acompanhar o seu pet shop ou clínica em
            qualquer fase do crescimento, 100% online e de qualquer lugar.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para todo tipo de
            <span class="text-kali-blue-600">negócio pet</span>
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Do pet shop de bairro à clínica veterinária com hospedagem, o
            Kali Pet se adapta à rotina de quem cuida de animais todos os
            dias.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Conheça o Kali Pet
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Tudo que o seu pet shop precisa, em um só sistema
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da agenda ao prontuário, cada funcionalidade foi pensada para
            tirar trabalho manual da sua rotina.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <div
              class="w-full h-44 rounded-2xl bg-gradient-to-br from-[#e6f0f4] to-[#b8dde8]"
            ></div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Pet acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, de forma
              automática e vinculada ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
            <ul class="mt-6 space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Entrada de Notas —</span>
                de forma automática, vinculado ao site da Receita Federal.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Integração —</span>
                com a maioria das prefeituras do Brasil, atendendo a legislação
                de todos os estados.
              </li>
            </ul>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Desenvolvido para MEI, ME, Simples, Lucro Real e Lucro
              Presumido, com emissão dos principais documentos fiscais.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Pet
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Pet a módulos adicionais e leve a gestão do seu
          pet shop ou clínica a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Tem prontuário de pacientes e vacinas?',
            answer:
              'Sim. Prontuário completo com histórico, anexos e controle de vacinas.',
          },
          {
            question: 'Agenda e serviços de banho & tosa?',
            answer:
              'Sim. Agendamentos, serviços e integração com vendas e estoque.',
          },
          {
            question: 'Controle de produtos e rações?',
            answer:
              'Sim. Gestão de estoque, etiquetas e categorias por marca e linha.',
          },
          {
            question: 'Emite notas fiscais?',
            answer: 'Sim. NF-e/NFC-e/NFS-e integradas ao financeiro.',
          },
        ]"
        description="Dúvidas sobre Kali Pet? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
